import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Layout } from "providers/Layout";
import { Link, useParams, useSearchParams } from "react-router-dom";
import addressApi from "api/landlord/address";
import { LayoutHeader } from "views/applications-id/components/LayoutHeader";
import { useEffect, useMemo, useState } from "react";
import { ActivityLog } from "../../shared/app/ActivityLog";
import { DecisionConfirmationModal } from "views/applications-id/components/DecisionConfirmationModal";
import { Applicant, Application } from "types/application";
import { Template } from "types/landlord/verificationTemplate";
import { Button, Show } from "shared/components";
import {
  ApplicationStatus,
  Invitee,
  Verification,
  VerificationNames,
  VerificationStatus
} from "shared/types";
import { combineVerifications } from "shared/utils";
import { TemplateReviewModal } from "shared/app/VerificationTemplate/modals/TemplateReviewModal";
import { CancelOrRefundModal } from "shared/app/CancelOrRefundModal";
import { useAuthContext } from "providers/Auth";
import { Redirect } from "shared/components/Redirect";
import { ApplicantStatus, ApplicantType } from "shared/types/application";
import { MakeDecisionModal } from "shared/app/MakeDecisionModal";
import { MoveModal } from "../../shared/app/MoveModal";
import { RefundModal } from "shared/app/RefundModal";
import { ApplicationActions } from "shared/utils/application-actions";
import { InviteAndCreateModal } from "shared/app/InviteAndCreateModal";
import { UncancelModal } from "shared/app/UncancelModal";
import { SplitModal } from "shared/app/SplitModal";
import { ChangeRoleModal } from "shared/app/ChangeRoleModal";
import { ResendInviteModal } from "shared/app/ResendInviteModal";
import { SetAsPrimaryModal } from "shared/app/SetAsPrimaryModal";
import { DocumentsRequestModal } from "shared/app/DocumentsRequestModal";
import { LoadingSkeletonSlice } from "./slices/LoadingSkeletonSlice";
import { Loader } from "shared/components/Loader";
import { ApplicationTab } from "views/applications-id/slices/application-tab";
import { Notes } from "shared/app/Notes";
import { useNotesController } from "shared/queries/useNotesController";
import { track } from "shared/telemetry/track";
import {
  NON_VERIFICATION_REPORT_VALUES,
  useApplicantReportState
} from "views/applications-id/hooks/use-applicant-report-state";
import { ApplicationReportLayout } from "views/applications-id-report/components/ApplicationReportLayout";
import { PrintProvider } from "views/applications-id-report/providers/PrintProvider";
import { mergeHousingHistoryVerifications } from "views/applications-id-report/utils/mergeHousingHistoryVerifications";
import {
  filterVerificationByConfigValue,
  sortVerifications,
  verificationNameToTitle
} from "views/applications-id/components/ApplicantsTable/utils";
import { VERIFICATIONS_ORDER } from "views/applications-id/components/ApplicantsTable/constants";
import { instance } from "api/apiBase";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { FEATURES } from "types/landlord/partner";

export type TabStepsType =
  | VerificationNames
  | "additional_documents"
  | "applicant";

// Tabs that exist for every applicant regardless of role or template. Used to
// decide whether the current report can be kept when switching applicants.
const ALWAYS_AVAILABLE_REPORTS: TabStepsType[] = [
  VerificationNames.RentalApplication,
  ...NON_VERIFICATION_REPORT_VALUES
];

// eslint-disable-next-line sonarjs/cognitive-complexity
export const ApplicationsIdOverview = () => {
  const [searchParams] = useSearchParams();
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [showUncancelModal, setShowUncancelModal] = useState<boolean>(false);
  const [showActivityLog, setShowActivityLog] = useState<boolean>(false);
  const { applicationId } = useParams();
  const [showDecisionFlowModal, setShowDecisionFlowModal] =
    useState<boolean>(false);
  const [showDecisionConfirmationModal, setDecisionConfirmationModal] =
    useState<boolean>(false);
  const [showInviteApplicantsModal, setShowInviteApplicantsModal] =
    useState<boolean>(false);

  const [showNotesModal, setShowNotesModal] = useState<boolean>(
    Boolean(searchParams.get("note_id"))
  );
  const [splitModalId, setSplitModalId] = useState<string | undefined>();

  const [previewTemplate, setPreviewTemplate] = useState<Template>();

  const [showMoveModal, setShowMoveModal] = useState<boolean>(false);
  const [setPrimaryModalApplicant, setSetPrimaryModalApplicant] = useState<
    Applicant | undefined
  >(undefined);
  const [changeRoleModalApplicant, setChangeRoleModalApplicant] = useState<
    Applicant | undefined
  >(undefined);
  const [resendInviteModalInvite, setResendInviteModalInvite] = useState<
    Invitee | undefined
  >(undefined);
  const [documentRequestModalApplicant, setDocumentRequestModalApplicant] =
    useState<string | undefined>(undefined);
  const queryClient = useQueryClient();

  const applicationMeta = useQuery<Application>({
    queryKey: ["APPLICATION", applicationId],
    queryFn: () => addressApi.getApplicationById(applicationId || "")
  });

  const {
    applicantDetail,
    applicationDecision: reportApplicationDecision,
    searchParams: reportSearchParams,
    setSearchParams: setReportSearchParams
  } = useApplicantReportState();

  const notesController = useNotesController(String(applicationId));

  useEffect(() => {
    if (!applicationId) return;
    track("application.page_view", {
      application_id: String(applicationId)
    });
  }, [applicationId]);

  const { type, permissions, partner } = useAuthContext();

  const {
    unit,
    property,
    applicants,
    status,
    submitted_at,
    verification_template,
    pending_magic_links,
    requires_overrides_identity_verification,
    decision_at
  } = applicationMeta.data || {};

  const canMakeDecision = useMemo(() => {
    return !new ApplicationActions({
      status: status || ApplicationStatus.Started,
      permissions,
      applicants: applicants || [],
      requirements: applicationMeta.data?.make_decision_requirements
    }).makeDecision.disabled;
  }, [
    requires_overrides_identity_verification,
    applicants,
    status,
    type,
    permissions
  ]);

  const notCancelledApplicants = useMemo(() => {
    return applicants?.filter(
      (applicant) => applicant.application_status !== ApplicantStatus.Canceled
    );
  }, [applicants]);

  // Only use the full applicant detail from the per-applicant endpoint.
  // The summary's applicants are slim (no verifications) and would crash
  // downstream components that expect verification data.
  const applicant = useMemo(() => {
    if (
      applicantDetail.data &&
      String(applicantDetail.data.id) === String(reportSearchParams.applicantId)
    )
      return applicantDetail.data;

    return undefined;
  }, [reportSearchParams.applicantId, applicantDetail.data]);

  const { mutate: changeRole } = useMutation({
    mutationKey: ["CHANGE_ROLE", applicationId],
    mutationFn: (role: ApplicantType) =>
      instance.post(
        `/portal/v1/applications/${applicationId}/applicants/${changeRoleModalApplicant?.id}/change_role`,
        {
          role: role
        }
      ),
    onSuccess: () => {
      toast.success("Role updated successfully");
      setChangeRoleModalApplicant(undefined);

      queryClient.invalidateQueries({
        queryKey: ["APPLICATION", applicationId]
      });
    },
    onError: (err: Error) => {
      toast.error(getErrorMessage(err));
      setChangeRoleModalApplicant(undefined);
    }
  });

  const availableRoles = useMemo(() => {
    const roles = [ApplicantType.Applicant];
    const settings = applicationMeta.data?.verification_template?.settings;
    if (settings?.co_signer?.enabled_screening_services)
      roles.push(ApplicantType.CoSigner);
    if (settings?.occupant?.enabled_screening_services)
      roles.push(ApplicantType.Occupant);
    return roles;
  }, [applicationMeta.data]);

  const verificationSchemas = applicant?.role
    ? applicationMeta.data?.verification_template?.verification_schemas[
        applicant.role
      ]
    : undefined;
  const applicationSchemaNames = Object.keys(verificationSchemas || {});
  // Applicant may use template that is different from the property template, which causes available reports discrepancy
  const hasVerificationsMismatch =
    (
      applicant?.verifications.filter(
        (v) => !applicationSchemaNames.includes(v.name)
      ) || []
    ).length > 0;

  const premiumRentalVerificationEnabled =
    partner?.features.includes(FEATURES.WIP_FEAT_PREMIUM_RENTAL_VERIFICATION) ??
    false;

  const applicationVerifications = useMemo(() => {
    if (!verificationSchemas) return [];
    if (hasVerificationsMismatch && applicant?.verifications) {
      if (!premiumRentalVerificationEnabled)
        return applicant.verifications.filter(
          (v) => v.name !== VerificationNames.PremiumRentalHistory
        );
      return applicant.verifications;
    }
    return Object.entries(verificationSchemas)
      .filter(([v, vValue]) => {
        if (
          !premiumRentalVerificationEnabled &&
          v === VerificationNames.PremiumRentalHistory
        )
          return false;
        // Warnings report has no applicant-facing step, so a schema entry
        // alone doesn't mean the report exists — show it only when the
        // applicant actually has one.
        if (
          v === VerificationNames.WarningsReport &&
          !applicant?.verifications.some(
            (av) => av.name === VerificationNames.WarningsReport
          )
        )
          return false;
        return filterVerificationByConfigValue({
          verificationName: v,
          verificationValue: vValue
        });
      })
      .map((v) => v[0])
      .map((name) => {
        const existingVerification = applicant?.verifications.find(
          (v) => v.name === name
        );
        if (!existingVerification)
          return {
            id: `missing-${name}`,
            name,
            title: verificationNameToTitle(name as VerificationNames),
            status: VerificationStatus.NoDataAvailable
          };
        else return existingVerification;
      });
  }, [
    applicant?.verifications,
    verificationSchemas,
    hasVerificationsMismatch,
    premiumRentalVerificationEnabled
  ]);

  const mergedNavVerifications = useMemo(() => {
    const filtered = (applicationVerifications as Verification[]).filter(
      (v) =>
        v.name !== VerificationNames.TermsAndConditions &&
        v.name !== VerificationNames.RentalVerification
    );
    return mergeHousingHistoryVerifications(filtered).filter(
      (v) => v.name !== VerificationNames.PremiumRentalHistory
    );
  }, [applicationVerifications]);

  const requestedReport =
    (reportSearchParams.report as TabStepsType) ||
    VerificationNames.RentalApplication;

  // Reports the currently selected applicant can actually view: their
  // role-driven verifications plus the always-available tabs.
  const availableReports = useMemo(() => {
    const names = new Set<TabStepsType>(ALWAYS_AVAILABLE_REPORTS);
    mergedNavVerifications.forEach((v) => names.add(v.name as TabStepsType));
    return names;
  }, [mergedNavVerifications]);

  const isRequestedReportAvailable = availableReports.has(requestedReport);

  // When switching applicants we keep the same report in the URL. If the newly
  // selected applicant doesn't have it (e.g. an occupant without income
  // verification), show the rental application, which every applicant has.
  const currentTab: TabStepsType = isRequestedReportAvailable
    ? requestedReport
    : VerificationNames.RentalApplication;

  // Keep the URL honest so refreshes / deep links land on a report the
  // applicant actually has. Guarded on `applicant` so it only runs once the
  // full detail (with reliable verifications) has loaded.
  useEffect(() => {
    if (!applicant || isRequestedReportAvailable) return;

    setReportSearchParams({
      ...reportSearchParams,
      report: VerificationNames.RentalApplication
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setReportSearchParams identity is unstable; reportSearchParams is current for the loaded applicant
  }, [applicant, isRequestedReportAvailable]);

  const currentVerification = mergedNavVerifications.find(
    (verification) => verification.name === currentTab
  );

  const requiresOverridesIdentityVerification = Boolean(
    reportApplicationDecision.data?.requires_overrides_identity_verification?.applicants.some(
      ({ id }) => id === applicant?.id
    )
  );

  const verifications = combineVerifications<Verification>(
    sortVerifications(mergedNavVerifications, VERIFICATIONS_ORDER),
    [
      [
        VerificationNames.IncomeVerification,
        VerificationNames.VerifiedIncomeVerification
      ]
    ]
  );

  // Scopes the cancel / uncancel / refund modals to the single selected
  // applicant while the individual report body is showing. On the group
  // overview it stays undefined so those modals keep their existing
  // application-wide (bulk) behavior.
  const selectedReportApplicantId =
    reportSearchParams.applicantId && applicant
      ? String(applicant.id)
      : undefined;

  if (!type.isBoomScreen) return <Redirect href="/" />;

  if (applicationMeta.isError)
    return (
      <Layout>
        <div>
          <h1 className="mb-4 text-lg font-medium">
            Error loading application
          </h1>

          <Link to="/applications">
            <Button>Go back</Button>
          </Link>
        </div>
      </Layout>
    );

  // handle loading state here
  if (applicationMeta.isLoading) return <LoadingSkeletonSlice />;
  if (!unit || !property || !applicationMeta.data) return null;

  return (
    <Layout
      hideSectionScroll={false}
      sectionClassName={reportSearchParams.applicantId ? undefined : "!p-0"}
      headerClassName="z-[2] bg-white border-b border-alpha-100"
      header={
        <LayoutHeader
          application={applicationMeta.data}
          disableDecisionFlow={!canMakeDecision}
          applicants={applicants}
          invitees={pending_magic_links || []}
          applicationId={applicationId || ""}
          requirements={applicationMeta.data.make_decision_requirements}
          setShowActivityLog={setShowActivityLog}
          setShowDecisionFlowModal={setShowDecisionFlowModal}
          applicationStatus={status || ApplicationStatus.Started}
          setDecisionConfirmationModal={setDecisionConfirmationModal}
          submittedAt={submitted_at || ""}
          setShowInviteApplicantsModal={setShowInviteApplicantsModal}
          onCancelClick={() => setShowCancelModal(true)}
          onUncancelClick={() => setShowUncancelModal(true)}
          onRefundClick={() => setShowRefundModal(true)}
          onMoveClick={() => setShowMoveModal(true)}
          onSplitClick={() => setSplitModalId(applicationId)}
          decisionAt={decision_at || ""}
          setShowNotesModal={setShowNotesModal}
          numberOfNotes={notesController.totalNotesCount}
          selectedApplicantId={reportSearchParams.applicantId}
          onSelectApplicant={(applicantId) =>
            setReportSearchParams({ ...reportSearchParams, applicantId })
          }
          canViewReports={permissions.canViewReports}
        />
      }
    >
      <Show if={!reportSearchParams.applicantId}>
        <ApplicationTab
          applicationId={applicationId || ""}
          applicationMeta={applicationMeta}
          canMakeDecision={canMakeDecision}
          notCancelledApplicants={notCancelledApplicants}
          setShowActivityLog={setShowActivityLog}
          setShowDecisionFlowModal={setShowDecisionFlowModal}
          setDecisionConfirmationModal={setDecisionConfirmationModal}
          onViewTemplate={() => {
            setPreviewTemplate(verification_template);
          }}
        />
      </Show>

      <Show
        if={
          permissions.canViewReports &&
          Boolean(reportSearchParams.applicantId) &&
          Boolean(applicant)
        }
      >
        <PrintProvider>
          <ApplicationReportLayout
            applicant={applicant as Applicant}
            currentTab={currentTab}
            currentVerification={currentVerification}
            verifications={verifications}
            // @ts-expect-error -- verificationSchemas is typed as a partial record upstream; ApplicationReportLayout expects the full VerificationNames-keyed shape, matching the pre-merge behavior in applications-id-report/index.tsx
            verificationSchemas={verificationSchemas}
            setShowCancelModal={setShowCancelModal}
            setShowUncancelModal={setShowUncancelModal}
            setShowRefundModal={setShowRefundModal}
            setChangeRoleModalApplicant={setChangeRoleModalApplicant}
            setSetPrimaryModalApplicant={setSetPrimaryModalApplicant}
            setResendInviteModalInvite={setResendInviteModalInvite}
            setDocumentRequestModalApplicant={setDocumentRequestModalApplicant}
            requiresOverridesIdentityVerification={
              requiresOverridesIdentityVerification
            }
          />
        </PrintProvider>
      </Show>

      <Show
        if={
          permissions.canViewReports &&
          Boolean(reportSearchParams.applicantId) &&
          !applicant
        }
      >
        <div className="flex h-full w-full items-center justify-center">
          <Loader />
        </div>
      </Show>

      <TemplateReviewModal
        opened={!!previewTemplate}
        onClose={() => setPreviewTemplate(undefined)}
        template={previewTemplate}
        applicationId={applicationId}
        applicationStatus={status}
        propertyId={property.id}
      />
      {/* Show removes unnecessary fetch request */}
      <Show if={showActivityLog}>
        <ActivityLog
          propertyName={property.name}
          showActivityLog={showActivityLog}
          setShowActivityLog={setShowActivityLog}
          applicationId={applicationId || ""}
          applicants={applicationMeta.data?.applicants}
        />
      </Show>
      <Show if={showDecisionFlowModal}>
        <MakeDecisionModal
          opened={showDecisionFlowModal}
          onClose={() => setShowDecisionFlowModal(false)}
          applicationId={applicationId || ""}
        />
      </Show>

      <DecisionConfirmationModal
        opened={showDecisionConfirmationModal}
        onContinue={() => {
          setDecisionConfirmationModal(false);
          setShowDecisionFlowModal(true);
        }}
        onClose={() => setDecisionConfirmationModal(false)}
      />
      <Show if={showInviteApplicantsModal}>
        <InviteAndCreateModal
          disabledRoles={
            [...(applicants || []), ...(pending_magic_links || [])].some(
              (applicant) =>
                [ApplicantType.Applicant, ApplicantType.Occupant].includes(
                  applicant.role as ApplicantType
                )
            )
              ? []
              : [ApplicantType.CoSigner]
          }
          open={showInviteApplicantsModal}
          setOpen={() => setShowInviteApplicantsModal(false)}
          applicationId={Number(applicationId)}
          propertyId={property.id}
          propertyName={property.name}
          unitId={unit.id}
          unitName={unit.title}
          onInviteRefreshKey={["APPLICATION", applicationId]}
        />
      </Show>

      <Show if={showNotesModal}>
        <Notes
          showNotes={showNotesModal}
          setShowNotes={setShowNotesModal}
          application={applicationMeta.data}
          notesController={notesController}
        />
      </Show>
      <Show if={Boolean(splitModalId)}>
        <SplitModal
          isOpen={Boolean(splitModalId)}
          applicationId={splitModalId ?? ""}
          onClose={() => setSplitModalId(undefined)}
        />
      </Show>
      <CancelOrRefundModal
        applicants={applicants}
        applicationId={showCancelModal ? String(applicationId) : ""}
        applicantId={selectedReportApplicantId}
        onClose={() => setShowCancelModal(false)}
        onSuccess={() => {
          applicationMeta.refetch();
        }}
      />
      <UncancelModal
        applicationId={showUncancelModal ? String(applicationId) : ""}
        applicantId={selectedReportApplicantId}
        onClose={() => setShowUncancelModal(false)}
        onSuccess={() => {
          applicationMeta.refetch();
        }}
      />
      <RefundModal
        applicationId={showRefundModal ? String(applicationId) : ""}
        applicantId={selectedReportApplicantId}
        onClose={() => setShowRefundModal(false)}
        queryKey={["APPLICATION", applicationId]}
      />
      <MoveModal
        applicationId={showMoveModal ? String(applicationId) : ""}
        onClose={() => setShowMoveModal(false)}
      />
      <Show if={Boolean(setPrimaryModalApplicant)}>
        <SetAsPrimaryModal
          applicantName={
            [
              setPrimaryModalApplicant?.first_name,
              setPrimaryModalApplicant?.last_name
            ]
              .filter(Boolean)
              .join(" ") ||
            setPrimaryModalApplicant?.email ||
            "Applicant"
          }
          applicantId={String(setPrimaryModalApplicant?.id || "")}
          applicationId={String(applicationId || "")}
          onClose={() => setSetPrimaryModalApplicant(undefined)}
          onSuccess={() => {
            queryClient.invalidateQueries({
              queryKey: ["APPLICATION", applicationId]
            });
            setSetPrimaryModalApplicant(undefined);
          }}
        />
      </Show>
      <Show if={Boolean(changeRoleModalApplicant)}>
        <ChangeRoleModal
          availableRoles={availableRoles}
          onClose={() => setChangeRoleModalApplicant(undefined)}
          onChange={(selectedRole) => {
            changeRole(selectedRole);
          }}
          applicantsList={applicationMeta.data?.applicants || []}
          isOpen={Boolean(changeRoleModalApplicant)}
          applicant={changeRoleModalApplicant}
        />
      </Show>
      <Show if={Boolean(resendInviteModalInvite)}>
        <ResendInviteModal
          onClose={() => setResendInviteModalInvite(undefined)}
          onSuccess={() => {
            queryClient.invalidateQueries({
              queryKey: ["APPLICATION", applicationId]
            });
            setResendInviteModalInvite(undefined);
          }}
          pendingMagicLink={resendInviteModalInvite as Invitee}
          applicationId={String(applicationId || "")}
          canCopyApplicationLink={permissions.canCopyApplicationLink}
        />
      </Show>
      <Show if={Boolean(documentRequestModalApplicant)}>
        <DocumentsRequestModal
          onClose={() => setDocumentRequestModalApplicant(undefined)}
          applicationId={String(applicationId || "")}
          applicantId={String(documentRequestModalApplicant)}
          queryKey={["APPLICATION", String(applicationId)]}
        />
      </Show>
    </Layout>
  );
};
